import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMe,
  listCollectionRuns,
  listConnectors,
  runConnector,
  setConnectorEnabled,
  type ConnectorView,
  type RunStatus,
} from "@/lib/api";

const STATUS_STYLE: Record<RunStatus, { label: string; icon: string; cls: string }> = {
  ok: { label: "Clean", icon: "fa-circle-check", cls: "text-success" },
  partial: { label: "Exceptions", icon: "fa-triangle-exclamation", cls: "text-warning" },
  failed: { label: "Failed", icon: "fa-circle-exclamation", cls: "text-danger" },
};

/**
 * Automated evidence collection. Collectors fetch and file artefacts; they
 * never prepare or review an occurrence, so the sign-off chain is unchanged.
 */
export function ComplianceConnectorsPage() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const { data: connectors, isLoading } = useQuery({
    queryKey: ["connectors"],
    queryFn: listConnectors,
  });
  const { data: runs } = useQuery({ queryKey: ["connector-runs"], queryFn: () => listCollectionRuns() });
  const [error, setError] = useState<string | null>(null);
  const isAdmin = me?.role === "admin";

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["connectors"] });
    void qc.invalidateQueries({ queryKey: ["connector-runs"] });
    void qc.invalidateQueries({ queryKey: ["compliance-evidence"] });
    void qc.invalidateQueries({ queryKey: ["compliance-timeline"] });
  };

  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      setConnectorEnabled(key, enabled),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const run = useMutation({
    mutationFn: (key: string) => runConnector(key),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div>
      <header className="mb-6">
        <h1 className="page-title">Evidence collectors</h1>
        <p className="mt-2 text-text-secondary">
          Pull the population out of the systems themselves instead of screenshotting it.
          Collectors file dated, hashed artefacts against the open occurrence — they never
          prepare or review it, so a human still runs the control and a second still checks it.
        </p>
      </header>

      <p className="mb-6 rounded-token-lg border border-border bg-bg-card px-4 py-2.5 text-xs text-text-muted">
        Credentials live in environment variables, never in this database — a compliance
        record that also held the keys to every system it audits would be a worse
        liability than the manual process it replaces.
      </p>

      {error && <p className="mb-3 text-xs text-danger">{error}</p>}

      {isLoading ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : (
        <div className="mb-8 space-y-3">
          {connectors?.map((c) => (
            <ConnectorCard
              key={c.key}
              c={c}
              isAdmin={isAdmin}
              busy={run.isPending || toggle.isPending}
              onToggle={(enabled) => toggle.mutate({ key: c.key, enabled })}
              onRun={() => run.mutate(c.key)}
            />
          ))}
        </div>
      )}

      <h2 className="mb-3 font-display text-lg font-semibold text-text-heading">Run history</h2>
      {!runs || runs.length === 0 ? (
        <p className="rounded-token-lg border border-border bg-bg-card px-4 py-6 text-sm text-text-secondary">
          Nothing collected yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="px-4 py-2.5 font-semibold">When</th>
                <th className="px-4 py-2.5 font-semibold">Connector</th>
                <th className="px-4 py-2.5 font-semibold">Result</th>
                <th className="px-4 py-2.5 font-semibold">Summary</th>
                <th className="px-4 py-2.5 font-semibold">Filed</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const s = STATUS_STYLE[r.status];
                return (
                  <tr key={r.id} className="border-b border-border-light align-top">
                    <td className="whitespace-nowrap px-4 py-2.5 text-text-muted">
                      {new Date(r.ranAt).toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-text-heading">
                      {r.connectorKey}
                    </td>
                    <td className={`whitespace-nowrap px-4 py-2.5 ${s.cls}`}>
                      <i className={`fa-solid ${s.icon} mr-1.5 text-[0.7rem]`} />
                      {s.label}
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary">
                      {r.summary}
                      {r.exceptions && r.exceptions.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {r.exceptions.map((x, i) => (
                            <li key={i} className="text-xs text-warning">
                              · {x}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                      {r.evidenceIds?.length ?? 0} artefact(s)
                      {r.attachedCount > 0 && (
                        <span className="text-text-muted"> · {r.attachedCount} attached</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConnectorCard({
  c,
  isAdmin,
  busy,
  onToggle,
  onRun,
}: {
  c: ConnectorView;
  isAdmin: boolean;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
  onRun: () => void;
}) {
  return (
    <div className="rounded-token-lg border border-border bg-bg-card p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-sm font-semibold text-text-heading">{c.label}</h2>
            {c.configured ? (
              <span className="rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-medium text-success">
                <i className="fa-solid fa-circle-check mr-1 text-[0.6rem]" />
                Ready
              </span>
            ) : (
              <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-medium text-warning">
                <i className="fa-solid fa-key mr-1 text-[0.6rem]" />
                Needs {c.missingEnv.join(", ")}
              </span>
            )}
            {c.controlRefs.map((ref) => (
              <span
                key={ref}
                className="rounded-full bg-bg-secondary px-2 py-0.5 text-[11px] text-text-muted"
              >
                {ref}
              </span>
            ))}
          </div>
          <p className="mt-1.5 text-sm text-text-secondary">{c.description}</p>
          <p className="mt-1 text-xs text-text-muted">
            {c.lastRunAt
              ? `Last run ${new Date(c.lastRunAt).toLocaleString()}`
              : "Never run"}
            {c.enabled ? " · runs nightly" : " · not scheduled"}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={c.enabled}
              disabled={!isAdmin || !c.configured}
              onChange={(e) => onToggle(e.target.checked)}
            />
            Nightly
          </label>
          <button
            onClick={onRun}
            disabled={!isAdmin || !c.configured || busy}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
            title={c.configured ? "Collect now" : `Set ${c.missingEnv.join(", ")} first`}
          >
            Collect now
          </button>
        </div>
      </div>
    </div>
  );
}
